import type { FC, ReactNode } from "react";
import React, { useMemo } from "react";

import styled from "@emotion/styled";
import { isApiLink, isAppLink, rewriteAppLink } from "lib/Urls";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { OpenBrowser } from "src/hybrid/components/Browser";
import reportErrorSentry from "src/lib/reportErrorSentry";
import FilesCubit from "src/state/FilesCubit/FilesCubit";
import { toast, tracker } from "src/state/state";

import {
  AppPopup,
  AppQueryPopupsController
} from "../AppQueryPopups/AppQueryPopupsBloc";
import { PathnameToPopupMap } from "../AppQueryPopups/AppQueryPopupsRedirects";
import {
  hashActionController,
  HASH_ACTION_TRIGGER_CLASS
} from "../HashActions";

interface Props {
  to?: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  children: ReactNode;
  style?: React.CSSProperties;
  className?: string;
  highlight?: boolean;
  target?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const StyledLink = styled(RouterLink as any)`
  color: var(
    --link-color,
    ${(p: { color?: string }): string => p.color ?? "#000"}
  );
  text-decoration: underline;

  &[data-highlight="true"] {
    color: var(--color-sunrise-blue, #80aeff);
    text-decoration: underline;
  }

  &.hash-action-trigger {
    cursor: pointer;
    color: var(--Accessible-Blue);
    text-decoration: none;
    font-style: normal;
    font-weight: 500;
    line-height: 140%;
    letter-spacing: -0.32px;
  }
`;

const POPUP_QUERY_PARAM = "popup";

const Link: FC<Props> = (props) => {
  const navigate = useNavigate();
  const [invalidLink, setInvalidLink] = React.useState(false);
  const target = useMemo(() => {
    const toHref = props.to ?? "";

    // return only the search params if link is an old popup url
    const popupTarget = PathnameToPopupMap[toHref];
    if (popupTarget) {
      const currentPath = window.location.pathname;
      const searchParams = new URLSearchParams();
      searchParams.set(POPUP_QUERY_PARAM, popupTarget);
      return `${currentPath}?${searchParams.toString()}`;
    }

    return toHref;
  }, [props.to]);

  const downloadFile = (url: URL) => {
    const pathParts = url.pathname.split("/");
    const id = pathParts[pathParts.length - 1];
    void FilesCubit.startFileDownload(id, "content-link");
  };

  const to = useMemo(() => {
    const cleanLink = rewriteAppLink(target);
    if (!cleanLink) {
      setInvalidLink(true);
    }
    return cleanLink;
  }, [target]);

  const url = new URL(to, window.location.href);

  const appLink = isAppLink(url) || url.pathname.startsWith("/app");
  const apiLink = isApiLink(url);

  const isDownloadFile =
    apiLink && url.href.includes("/files") && !url.href.includes("/token");

  const isPopup = url.href.includes("dialog=true");

  const isHashOnly = to.startsWith("#") && to.length > 1;

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    props.onClick?.(e);

    if (invalidLink) {
      toast.show("error.invalidLink");
      reportErrorSentry(new Error("Invalid link clicked"), {
        url: to,
        target: e.currentTarget
      });
      return;
    }

    tracker.track("App Link Clicked", {
      data: {
        linkTarget: to,
        isAppLink: appLink,
        isDownloadFile,
        isPopup,
        isHashOnly
      }
    });

    if (isHashOnly) {
      hashActionController.open(to);
      return;
    }

    if (isDownloadFile) {
      downloadFile(url);
    } else if (appLink) {
      navigate(to);
    } else if (isPopup) {
      const targetInnerText = e.currentTarget.innerText;
      AppQueryPopupsController.openPopup(AppPopup.iframe, {
        additionalParameters: {
          url: url.href,
          title: targetInnerText,
          stay: "false"
        }
      });
    } else {
      void OpenBrowser(url.href, {
        useBaseUrl: false,
        presentationStyle: "fullscreen"
      });
    }
  };

  const className = [props.className, isHashOnly && HASH_ACTION_TRIGGER_CLASS]
    .filter(Boolean)
    .join(" ");

  return (
    <StyledLink
      data-highlight={props.highlight}
      {...props}
      className={className || undefined}
      to={to}
      onClick={handleClick}
      target={appLink ? undefined : "_blank"}
    >
      {props.children}
    </StyledLink>
  );
};

export default Link;
